import { useEffect, lazy, Suspense } from 'react';
import type { ReactElement } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { initializeTheme, useAuthStore, useAuth, useOnboardingStore } from './stores';
import { ToastContainer } from './components/ui';
import { ErrorBoundary, AppShell } from './components/layout';
import { PWAUpdatePrompt } from './components/features/pwa/PWAUpdatePrompt';
import { OfflineBanner } from './components/features/pwa/OfflineBanner';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';

// Eager-loaded pages (immediate need - auth & core flow)
import {
  OnboardingPage,
  DashboardPage,
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  MorningCheckinPage,
  NightlyReviewPage,
} from './pages';
import LandingPage from './components/landing/LandingPage';

// Lazy-loaded pages (feature pages - load on demand)
const JournalPage = lazy(() => import('./pages/JournalPage'));
const ReadingsPage = lazy(() => import('./pages/ReadingsPage'));
const SponsorDashboardPage = lazy(() => import('./pages/SponsorDashboardPage'));
const SponsorHomeworkPage = lazy(() => import('./pages/SponsorHomeworkPage'));
const SponsorStepReviewPage = lazy(() => import('./pages/SponsorStepReviewPage'));
const StepWorkPage = lazy(() => import('./pages/StepWorkPage'));
const StepDetailPage = lazy(() => import('./pages/StepDetailPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const ProfileSettingsPage = lazy(() => import('./pages/settings/ProfileSettingsPage'));
const ProgramsSettingsPage = lazy(() => import('./pages/settings/ProgramsSettingsPage'));
const SobrietyDatesSettingsPage = lazy(() => import('./pages/settings/SobrietyDatesSettingsPage'));
const SobrietyDateFormPage = lazy(() => import('./pages/settings/SobrietyDateFormPage'));
const NotificationsSettingsPage = lazy(() => import('./pages/settings/NotificationsSettingsPage'));
const SponsorSettingsPage = lazy(() => import('./pages/settings/SponsorSettingsPage'));
const PrivacySettingsPage = lazy(() => import('./pages/settings/PrivacySettingsPage'));
const ActivitySettingsPage = lazy(() => import('./pages/settings/ActivitySettingsPage'));
const ThreeCirclesSettingsPage = lazy(() => import('./pages/settings/ThreeCirclesSettingsPage'));
const ActivityLogPage = lazy(() => import('./pages/ActivityLogPage'));
const BillingPage = lazy(() => import('./pages/settings/BillingPage'));

// Loading spinner component
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-accent-200 dark:border-accent-800 border-t-accent-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-surface-500 dark:text-surface-400">Loading...</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { isAuthenticated, isInitialized, isOnboardingReady } = useAuth();
  const hasCompletedOnboarding = useOnboardingStore((state) => state.hasCompletedOnboarding);
  useKeyboardShortcuts();

  // Auth guard + AppShell wrapper for all authenticated pages
  function authed(element: ReactElement): ReactElement {
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (!hasCompletedOnboarding) return <Navigate to="/onboarding" replace />;
    return <AppShell>{element}</AppShell>;
  }

  // Show loading until Firebase auth AND the Firestore onboarding-status check are both complete.
  // On a new device (no localStorage), isOnboardingReady stays false until the Firestore profile
  // fetch resolves, preventing the race condition that redirected users to /onboarding prematurely.
  if (!isInitialized || !isOnboardingReady) {
    return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
      {/* Public auth routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/register"
        element={isAuthenticated ? <Navigate to="/" replace /> : <RegisterPage />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Protected routes */}
      <Route
        path="/onboarding"
        element={isAuthenticated ? <OnboardingPage /> : <Navigate to="/login" replace />}
      />
      <Route path="/dashboard" element={authed(<DashboardPage />)} />
      <Route path="/checkin" element={authed(<MorningCheckinPage />)} />
      <Route path="/nightly-review" element={authed(<NightlyReviewPage />)} />
      <Route path="/journal" element={authed(<JournalPage />)} />
      <Route path="/read" element={authed(<ReadingsPage />)} />
      <Route path="/sponsor-dashboard" element={authed(<SponsorDashboardPage />)} />
      <Route path="/sponsor/homework" element={authed(<SponsorHomeworkPage />)} />
      <Route
        path="/sponsor/review/:protegeId/:fellowship/:stepNumber"
        element={authed(<SponsorStepReviewPage />)}
      />
      <Route path="/steps" element={authed(<StepWorkPage />)} />
      <Route path="/steps/:fellowship/:stepNumber" element={authed(<StepDetailPage />)} />
      <Route path="/settings" element={authed(<SettingsPage />)} />
      <Route path="/settings/profile" element={authed(<ProfileSettingsPage />)} />
      <Route path="/settings/programs" element={authed(<ProgramsSettingsPage />)} />
      <Route path="/settings/sobriety-dates" element={authed(<SobrietyDatesSettingsPage />)} />
      <Route path="/settings/sobriety-dates/add" element={authed(<SobrietyDateFormPage />)} />
      <Route
        path="/settings/sobriety-dates/edit/:dateId"
        element={authed(<SobrietyDateFormPage />)}
      />
      <Route path="/settings/notifications" element={authed(<NotificationsSettingsPage />)} />
      <Route path="/settings/sponsor" element={authed(<SponsorSettingsPage />)} />
      <Route path="/settings/activity-log" element={authed(<ActivitySettingsPage />)} />
      <Route path="/settings/three-circles" element={authed(<ThreeCirclesSettingsPage />)} />
      <Route path="/settings/privacy" element={authed(<PrivacySettingsPage />)} />
      <Route path="/settings/billing" element={authed(<BillingPage />)} />
      <Route path="/meetings" element={authed(<MeetingsPage />)} />
      <Route path="/activity-log" element={authed(<ActivityLogPage />)} />

      {/* Root route - landing page for unauthenticated, dashboard for authenticated */}
      <Route
        path="/"
        element={
          !isAuthenticated ? (
            <LandingPage />
          ) : hasCompletedOnboarding ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/onboarding" replace />
          )
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  const initialize = useAuthStore((state) => state.initialize);

  useEffect(() => {
    const themeCleanup = initializeTheme();
    const authCleanup = initialize();

    return () => {
      themeCleanup?.();
      authCleanup();
    };
  }, [initialize]);

  return (
    <BrowserRouter>
      {/* Skip link for keyboard navigation */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <OfflineBanner />
      <AppRoutes />
      <ToastContainer />
      <PWAUpdatePrompt />
    </BrowserRouter>
  );
}

export default App;
